import { useState, useEffect, useCallback, useRef } from "react";
import {
  Car, Plus, Loader2, Trash2, Pencil, ArrowLeft, Save, ImagePlus, X, Eye, EyeOff,
  GripVertical, Upload, ArrowUp, ArrowDown, Layers, Sparkles, Link2, FileText, RefreshCw,
} from "lucide-react";
import { API, apiFetch, uploadFile } from "@/portal/api";
import { toast } from "sonner";
import { confirmDialog } from "@/portal/confirm";
import { imgV } from "@/data/vehicles";

const adminImg = (url, v) => { const u = imgV(url, v); return u.startsWith("/api") ? `${API.replace("/api", "")}${u}` : u; };

const emptyVehicle = {
  name: "", year: "", bodyType: "", priceMonthly: "", priceOriginal: "", promoLabel: "",
  competitorRental: "", competitorLease: "", competitors: [], fuel: "", transmission: "",
  mileage: "", description: "", multipleAvailable: true, published: true, featureGroups: [], slug: "",
  seats: "", powerKw: "", trim: "", compareVerdict: "",
};

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const VehiclesAdminTab = ({ token }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // vehicle object or null
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [aiSource, setAiSource] = useState("url"); // url | text | file
  const [aiUrl, setAiUrl] = useState("");
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const aiFileRef = useRef(null);
  const [syncing, setSyncing] = useState(false);
  const [deduping, setDeduping] = useState(false);
  const [refreshingComp, setRefreshingComp] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [dragId, setDragId] = useState(null);

  const onDropVehicle = async (targetId) => {
    const src = dragId;
    setDragId(null);
    if (!src || src === targetId) return;
    const ids = list.map((x) => x.id);
    const from = ids.indexOf(src), to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setList(ids.map((id) => list.find((x) => x.id === id)));
    try { await apiFetch("/admin/vehicles/reorder", { method: "PUT", token, body: { ordered_ids: ids } }); }
    catch (e) { toast.error(e.message); load(); }
  };

  const optimizeImages = async () => {
    setOptimizing(true);
    try {
      const res = await apiFetch("/admin/vehicles/optimize-images", { method: "POST", token });
      toast.success(`Fotky optimalizovány: ${res.optimized} zmenšeno, ${res.skipped} už bylo OK${res.failed ? `, ${res.failed} selhalo` : ""}.`);
    } catch (e) { toast.error(e.message); }
    finally { setOptimizing(false); }
  };

  const refreshAllCompetitors = async () => {
    setRefreshingComp(true);
    try {
      const res = await apiFetch("/admin/vehicles/refresh-competitors", { method: "POST", token });
      toast.success(`Obnova srovnání běží na pozadí pro ${res.vehicles} vozů (hledá se na webu, potrvá pár minut).`);
      setTimeout(load, 90000);
    } catch (e) { toast.error(e.message); }
    finally { setRefreshingComp(false); }
  };
  const [compLoading, setCompLoading] = useState(false);
  const [applyingPl, setApplyingPl] = useState(false);
  const applyPricelist = async () => {
    if (!window.confirm("Nastavit u vozů na webu ceny (vč. DPH) podle finálního ceníku EffectDeal? Změní se jen auta, která se podaří spárovat podle názvu modelu.")) return;
    setApplyingPl(true);
    try {
      const res = await apiFetch("/admin/vehicles/apply-pricelist", { method: "POST", token });
      const c = res.counts || {};
      toast.success(`Ceník nasazen: ${c.updated} aut aktualizováno, ${c.unchanged} beze změny.`);
      if (res.unmatched?.length) toast.warning(`Ručně nastav (nespárováno): ${res.unmatched.join(", ")}`, { duration: 10000 });
      load();
    } catch (e) { toast.error(e.message); }
    finally { setApplyingPl(false); }
  };

  const aiCompetitor = async () => {
    if (!editing?.id) return;
    setCompLoading(true);
    try {
      const updated = await apiFetch(`/admin/vehicles/${editing.id}/ai-competitor`, { method: "POST", token });
      setEditing((e) => ({
        ...e,
        competitorRental: updated.competitorRental ?? "",
        competitorLease: updated.competitorLease ?? "",
        competitors: updated.competitors || [],
      }));
      toast.success(`AI odhadla ceny ${(updated.competitors || []).length} konkurentů – zkontrolujte a případně upravte.`);
    } catch (e) { toast.error(e.message); }
    finally { setCompLoading(false); }
  };

  const syncFleet = async () => {
    setSyncing(true);
    try {
      const res = await apiFetch("/admin/vehicles/sync-fleet", { method: "POST", token });
      toast.success(`Propsáno ${res.synced} vozů do vozového parku.`);
    } catch (e) { toast.error(e.message); }
    finally { setSyncing(false); }
  };

  const dedupeFleet = async () => {
    const ok = await confirmDialog({
      title: "Odstranit duplicitní vozy?",
      description: "Sloučí auta se stejnou značkou a modelem ve Vozovém parku (ponechá to s SPZ / aktivní smlouvou) a smaže testovací a osiřelé záznamy. Vozy navázané na smlouvu se nikdy nesmažou.",
      confirmText: "Odstranit duplicity",
    });
    if (!ok) return;
    setDeduping(true);
    try {
      const res = await apiFetch("/admin/cars/dedupe", { method: "POST", token });
      toast.success(res.removed > 0 ? `Odstraněno ${res.removed} duplicitních vozů.` : "Žádné duplicity nenalezeny.");
    } catch (e) { toast.error(e.message); }
    finally { setDeduping(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { setList(await apiFetch("/admin/vehicles", { token })); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const reloadEditing = async (id) => {
    const all = await apiFetch("/admin/vehicles", { token });
    setList(all);
    const v = all.find((x) => x.id === id);
    if (v) setEditing(v);
  };

  const startNew = () => setEditing({ ...emptyVehicle });
  const startEdit = (v) => setEditing({ ...v, featureGroups: v.featureGroups || [] });

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Zadejte název vozu."); return; }
    setSaving(true);
    const payload = {
      name: editing.name, year: num(editing.year), bodyType: editing.bodyType || null,
      priceMonthly: num(editing.priceMonthly), fuel: editing.fuel || null,
      priceOriginal: num(editing.priceOriginal), promoLabel: editing.promoLabel || null,
      competitorRental: num(editing.competitorRental), competitorLease: num(editing.competitorLease),
      competitors: (editing.competitors || [])
        .map((c) => ({ name: (c.name || "").trim(), type: c.type === "lease" ? "lease" : "rental", price: num(c.price), note: (c.note || "").trim() || null,
          year: num(c.year), powerKw: num(c.powerKw), trim: (c.trim || "").trim() || null, mileage: (c.mileage || "").trim() || null,
          term: (c.term || "").trim() || null, deposit: (c.deposit || "").trim() || null, kmIncluded: (c.kmIncluded || "").trim() || null }))
        .filter((c) => c.name && c.price),
      compareVerdict: (editing.compareVerdict || "").trim() || null,
      seats: num(editing.seats), powerKw: num(editing.powerKw), trim: (editing.trim || "").trim() || null,
      transmission: editing.transmission || null, mileage: editing.mileage || null,
      description: editing.description || null, multipleAvailable: !!editing.multipleAvailable,
      published: !!editing.published,
      featureGroups: (editing.featureGroups || [])
        .map((g) => ({ title: (g.title || "").trim(), items: (g.items || []).map((i) => i.trim()).filter(Boolean) }))
        .filter((g) => g.title || g.items.length),
    };
    try {
      if (editing.id) {
        const updated = await apiFetch(`/admin/vehicles/${editing.id}`, { method: "PUT", token, body: payload });
        setEditing({ ...updated, featureGroups: updated.featureGroups || [] });
        toast.success("Vůz uložen.");
      } else {
        const created = await apiFetch("/admin/vehicles", { method: "POST", token, body: payload });
        setEditing({ ...created, featureGroups: created.featureGroups || [] });
        toast.success("Vůz vytvořen. Teď můžete přidat fotky.");
      }
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const del = async (v) => {
    if (!(await confirmDialog({ title: `Smazat ${v.name}?`, description: "Vůz i jeho fotky budou nenávratně odstraněny z webu." }))) return;
    try { await apiFetch(`/admin/vehicles/${v.id}`, { method: "DELETE", token }); toast.success("Vůz smazán."); load(); }
    catch (e) { toast.error(e.message); }
  };

  const move = async (v, dir) => {
    const ids = list.map((x) => x.id); const i = ids.indexOf(v.id); const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try { await apiFetch("/admin/vehicles/reorder", { method: "PUT", token, body: { ordered_ids: ids } }); load(); } catch (e) { toast.error(e.message); }
  };
  const togglePublish = async (v) => {
    try {
      await apiFetch(`/admin/vehicles/${v.id}`, { method: "PUT", token, body: { ...v, published: !v.published, featureGroups: v.featureGroups || [] } });
      load();
    } catch (e) { toast.error(e.message); }
  };

  // ---- Images ----
  const onUpload = async (files) => {
    if (!editing?.id || !files?.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of Array.from(files)) {
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name}: příliš velké (max 15 MB)`); continue; }
      try { await uploadFile(`/admin/vehicles/${editing.id}/images`, f, token, { label: "", alt: editing.name }); ok++; }
      catch (e) { toast.error(`${f.name}: ${e.message}`); }
    }
    if (ok) toast.success(`Nahráno ${ok} fotek.`);
    await reloadEditing(editing.id);
    setUploading(false);
  };

  const delImage = async (imgId) => {
    try { await apiFetch(`/admin/vehicles/${editing.id}/images/${imgId}`, { method: "DELETE", token }); await reloadEditing(editing.id); }
    catch (e) { toast.error(e.message); }
  };

  const moveImage = async (idx, dir) => {
    const imgs = [...editing.images];
    const j = idx + dir;
    if (j < 0 || j >= imgs.length) return;
    [imgs[idx], imgs[j]] = [imgs[j], imgs[idx]];
    setEditing({ ...editing, images: imgs });
    try { await apiFetch(`/admin/vehicles/${editing.id}/images/reorder`, { method: "PUT", token, body: { ordered_ids: imgs.map((i) => i.id) } }); }
    catch (e) { toast.error(e.message); reloadEditing(editing.id); }
  };

  const saveLabel = async (imgId, label) => {
    try { await apiFetch(`/admin/vehicles/${editing.id}/images/${imgId}`, { method: "PUT", token, body: { label } }); }
    catch (e) { toast.error(e.message); }
  };

  // ---- Feature groups helpers ----
  const fg = editing?.featureGroups || [];
  const setFg = (groups) => setEditing({ ...editing, featureGroups: groups });
  const addGroup = () => setFg([...fg, { title: "", items: [""] }]);
  const removeGroup = (gi) => setFg(fg.filter((_, i) => i !== gi));
  const setGroupTitle = (gi, title) => setFg(fg.map((g, i) => (i === gi ? { ...g, title } : g)));
  const addItem = (gi) => setFg(fg.map((g, i) => (i === gi ? { ...g, items: [...(g.items || []), ""] } : g)));
  const setItem = (gi, ii, val) => setFg(fg.map((g, i) => (i === gi ? { ...g, items: g.items.map((it, k) => (k === ii ? val : it)) } : g)));
  const removeItem = (gi, ii) => setFg(fg.map((g, i) => (i === gi ? { ...g, items: g.items.filter((_, k) => k !== ii) } : g)));

  // ---- AI auto-fill equipment (URL or PDF) ----
  const applyAiResult = async (data) => {
    const groups = (data.featureGroups || []).filter((g) => g.title && (g.items || []).length);
    if (!groups.length) { toast.error("AI nenašla žádnou výbavu."); return; }
    const hasExisting = (editing.featureGroups || []).some((g) => (g.items || []).some((it) => (it || "").trim()));
    let keepFeatures = false;
    if (hasExisting) {
      const overwrite = await confirmDialog({
        title: "Přepsat stávající výbavu?",
        description: "Vůz už má vyplněnou výbavu. Chcete ji nahradit tou, kterou našla AI?",
        confirmText: "Přepsat", cancelText: "Nechat stávající",
      });
      keepFeatures = !overwrite;
    }
    const next = { ...editing };
    if (!keepFeatures) next.featureGroups = groups;
    // fill basic fields only when AI found a value and the field is empty
    const setIfEmpty = (k, v) => { if (v && !String(next[k] ?? "").trim()) next[k] = v; };
    setIfEmpty("name", data.name);
    setIfEmpty("year", data.year);
    setIfEmpty("fuel", data.fuel);
    setIfEmpty("transmission", data.transmission);
    setIfEmpty("bodyType", data.bodyType);
    setIfEmpty("mileage", data.mileage);
    setIfEmpty("description", data.description);
    setEditing(next);
    toast.success("Výbava vyplněna. Zkontrolujte a upravte podle potřeby.");
  };

  const aiFillFromUrl = async () => {
    if (!aiUrl.trim()) { toast.error("Vložte odkaz na inzerát."); return; }
    await aiFillPost({ url: aiUrl.trim() });
  };

  const aiFillFromText = async () => {
    if (aiText.trim().length < 20) { toast.error("Vložte více textu s výbavou."); return; }
    await aiFillPost({ text: aiText.trim() });
  };

  const aiFillPost = async (fields) => {
    setAiLoading(true);
    try {
      const fd = new FormData();
      Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${API}/admin/vehicles/ai-fill`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      let data = null;
      try { data = await res.json(); } catch { data = null; }
      if (!res.ok || !data) {
        throw new Error(data?.detail || "Zdroj se nepodařilo zpracovat. Zkuste možnost PDF nebo Text (vložte výbavu z AI asistenta).");
      }
      await applyAiResult(data);
    } catch (e) { toast.error(e.message); }
    finally { setAiLoading(false); }
  };

  const aiFillFromFile = async (file) => {
    if (!file) return;
    setAiLoading(true);
    try {
      const data = await uploadFile("/admin/vehicles/ai-fill", file, token);
      await applyAiResult(data);
    } catch (e) { toast.error(e.message); }
    finally { setAiLoading(false); if (aiFileRef.current) aiFileRef.current.value = ""; }
  };

  if (editing) {
    return (
      <div className="space-y-6" data-testid="vehicle-editor">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button onClick={() => { setEditing(null); load(); }} className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white" data-testid="vehicle-back-btn">
            <ArrowLeft className="w-4 h-4" /> Zpět na seznam
          </button>
          <button onClick={save} disabled={saving} className="btn-accent inline-flex items-center gap-2 disabled:opacity-60" data-testid="vehicle-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Uložit vůz
          </button>
        </div>

        <div className="glass rounded-2xl p-5 border border-white/10">
          <h3 className="font-['Cabinet_Grotesk'] font-bold mb-4">{editing.id ? "Upravit vůz" : "Nový vůz"}</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs text-zinc-500 mb-1">Název vozu *</label>
              <input className="input-dark" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="např. Hyundai Tucson" data-testid="vehicle-name" />
            </div>
            <div><label className="block text-xs text-zinc-500 mb-1">Rok</label><input type="number" className="input-dark" value={editing.year ?? ""} onChange={(e) => setEditing({ ...editing, year: e.target.value })} placeholder="2025" data-testid="vehicle-year" /></div>
            <div><label className="block text-xs text-zinc-500 mb-1">Karoserie</label><input className="input-dark" value={editing.bodyType ?? ""} onChange={(e) => setEditing({ ...editing, bodyType: e.target.value })} placeholder="SUV / Kombi / Hatchback" data-testid="vehicle-bodytype" /></div>
            <div><label className="block text-xs text-zinc-500 mb-1">Cena / měsíc vč. DPH (Kč) — prázdné = na dotaz</label><input type="number" className="input-dark" value={editing.priceMonthly ?? ""} onChange={(e) => setEditing({ ...editing, priceMonthly: e.target.value })} placeholder="17500" data-testid="vehicle-price" />{editing.priceMonthly ? <p className="text-[11px] text-zinc-500 mt-1">= {Math.round(Number(editing.priceMonthly) / 1.21).toLocaleString("cs-CZ")} Kč bez DPH</p> : null}</div>
            <div><label className="block text-xs text-zinc-500 mb-1">Původní (škrtlá) cena — akce</label><input type="number" className="input-dark" value={editing.priceOriginal ?? ""} onChange={(e) => setEditing({ ...editing, priceOriginal: e.target.value })} placeholder="např. 20500 → na webu škrtnutá" data-testid="vehicle-price-original" /></div>
            <div><label className="block text-xs text-zinc-500 mb-1">Štítek akce (nepovinné)</label><input className="input-dark" value={editing.promoLabel ?? ""} onChange={(e) => setEditing({ ...editing, promoLabel: e.target.value })} placeholder="AKCE / Sleva 15 % / Letní nabídka" data-testid="vehicle-promo-label" /></div>
            <div><label className="block text-xs text-zinc-500 mb-1">Konkurence – půjčovna (Kč/měs, nejnižší)</label><input type="number" className="input-dark" value={editing.competitorRental ?? ""} onChange={(e) => setEditing({ ...editing, competitorRental: e.target.value })} placeholder="24500" data-testid="vehicle-competitor-rental" /></div>
            <div><label className="block text-xs text-zinc-500 mb-1">Konkurence – operák (Kč/měs, nejnižší)</label><input type="number" className="input-dark" value={editing.competitorLease ?? ""} onChange={(e) => setEditing({ ...editing, competitorLease: e.target.value })} placeholder="13200" data-testid="vehicle-competitor-lease" /></div>
            <div><label className="block text-xs text-zinc-500 mb-1">Nájezd</label><input className="input-dark" value={editing.mileage ?? ""} onChange={(e) => setEditing({ ...editing, mileage: e.target.value })} placeholder="cca 14 000 km" data-testid="vehicle-mileage" /></div>
            <div><label className="block text-xs text-zinc-500 mb-1">Palivo</label><input className="input-dark" value={editing.fuel ?? ""} onChange={(e) => setEditing({ ...editing, fuel: e.target.value })} placeholder="Benzín / Nafta 2.0 TDI" data-testid="vehicle-fuel" /></div>
            <div><label className="block text-xs text-zinc-500 mb-1">Převodovka</label><input className="input-dark" value={editing.transmission ?? ""} onChange={(e) => setEditing({ ...editing, transmission: e.target.value })} placeholder="Automat / Manuál" data-testid="vehicle-transmission" /></div>
            <div><label className="block text-xs text-zinc-500 mb-1">Výkon (kW)</label><input type="number" className="input-dark" value={editing.powerKw ?? ""} onChange={(e) => setEditing({ ...editing, powerKw: e.target.value })} placeholder="110" data-testid="vehicle-power" /></div>
            <div><label className="block text-xs text-zinc-500 mb-1">Počet míst</label><input type="number" className="input-dark" value={editing.seats ?? ""} onChange={(e) => setEditing({ ...editing, seats: e.target.value })} placeholder="5" data-testid="vehicle-seats" /></div>
            <div className="sm:col-span-2"><label className="block text-xs text-zinc-500 mb-1">Výbava / generace / motor (zobrazí se na kartě i ve srovnávači)</label><input className="input-dark" value={editing.trim ?? ""} onChange={(e) => setEditing({ ...editing, trim: e.target.value })} placeholder="např. II. generace · Laurin & Klement · 2.0 TDI 4x4" data-testid="vehicle-trim" /></div>
            <div className="sm:col-span-2"><label className="block text-xs text-zinc-500 mb-1">Popis (nepovinné)</label><textarea rows={2} className="input-dark w-full resize-none" value={editing.description ?? ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="vehicle-description" /></div>
          </div>

          {/* Competitor comparison editor */}
          <div className="mt-5 rounded-xl border border-indigo-500/20 bg-indigo-500/[0.04] p-4" data-testid="competitors-editor">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <p className="text-sm font-semibold text-indigo-300">Srovnání s konkurencí (zobrazí se na detailu vozu)</p>
              {editing.id ? (
                <button type="button" onClick={aiCompetitor} disabled={compLoading} className="inline-flex items-center gap-1.5 text-xs px-3.5 py-2 rounded-full bg-indigo-500 text-white font-semibold hover:bg-indigo-400 disabled:opacity-60" data-testid="vehicle-ai-competitor">
                  {compLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} AI odhad – 10 půjčoven + 8 leasingovek
                </button>
              ) : (
                <span className="text-xs text-zinc-500">Nejdřív vůz uložte – AI odhad se pak spustí automaticky.</span>
              )}
            </div>
            <p className="text-xs text-zinc-500 mb-3">
              Půjčovny (Taggart, Finecar, EasyRent…) i operáky (Drivalia, Driveto, Ayvens, Arval…). Značkové financování se nabízí jen pro odpovídající značku, u vozů starších 3 let se operák nesrovnává. Ceny můžete ručně upravit, přidat nebo smazat.
              {editing.competitorsUpdatedAt ? ` Naposledy aktualizováno ${new Date(editing.competitorsUpdatedAt).toLocaleDateString("cs-CZ")} (obnovuje se automaticky každé pondělí).` : ""}
            </p>
            {(editing.competitors || []).length > 0 && (
              <div className="space-y-1.5 mb-3">
                {editing.competitors.map((c, ci) => (
                  <div key={ci} className="rounded-lg border border-white/5 p-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-28 shrink-0">
                      <select className="input-dark text-xs w-full" value={c.type === "lease" ? "lease" : "rental"} onChange={(e) => setEditing({ ...editing, competitors: editing.competitors.map((x, i) => (i === ci ? { ...x, type: e.target.value } : x)) })} data-testid={`competitor-type-${ci}`}>
                        <option value="rental">Půjčovna</option>
                        <option value="lease">Operák</option>
                      </select>
                    </div>
                    <input className="input-dark text-xs flex-1 min-w-0" placeholder="Název (např. Taggart)" value={c.name || ""} onChange={(e) => setEditing({ ...editing, competitors: editing.competitors.map((x, i) => (i === ci ? { ...x, name: e.target.value } : x)) })} data-testid={`competitor-name-${ci}`} />
                    <input className="input-dark text-xs flex-1 min-w-0" placeholder="Ročník / výbava (např. 2021, Style)" value={c.note || ""} onChange={(e) => setEditing({ ...editing, competitors: editing.competitors.map((x, i) => (i === ci ? { ...x, note: e.target.value } : x)) })} data-testid={`competitor-note-${ci}`} />
                    <div className="w-28 shrink-0">
                      <input type="number" className="input-dark text-xs w-full" placeholder="Kč/měs" value={c.price ?? ""} onChange={(e) => setEditing({ ...editing, competitors: editing.competitors.map((x, i) => (i === ci ? { ...x, price: e.target.value } : x)) })} data-testid={`competitor-price-${ci}`} />
                    </div>
                    <button type="button" onClick={() => setEditing({ ...editing, competitors: editing.competitors.filter((_, i) => i !== ci) })} className="text-zinc-500 hover:text-red-400 p-1 shrink-0" data-testid={`competitor-del-${ci}`}><X className="w-4 h-4" /></button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mt-1.5 mb-2.5 pl-1" data-testid={`competitor-extra-${ci}`}>
                    {[["year", "Ročník", "2023"], ["powerKw", "kW", "110"], ["trim", "Výbava / motor", "Style 1.5 TSI"], ["mileage", "Nájezd", "cca 60 000 km"], ["term", "Úvazek", "36 měs. / bez úvazku"], ["deposit", "Akontace", "0 Kč / 10 %"], ["kmIncluded", "Km v ceně", "10 000 km/rok"]].map(([k, lbl, ph]) => (
                      <input key={k} type={k === "year" || k === "powerKw" ? "number" : "text"} className="input-dark text-xs w-full" placeholder={`${lbl} (${ph})`} title={lbl} value={c[k] ?? ""} onChange={(e) => setEditing({ ...editing, competitors: editing.competitors.map((x, i) => (i === ci ? { ...x, [k]: e.target.value } : x)) })} data-testid={`competitor-${k}-${ci}`} />
                    ))}
                  </div>
                  </div>
                ))}
              </div>
            )}
            <button type="button" onClick={() => setEditing({ ...editing, competitors: [...(editing.competitors || []), { name: "", type: "rental", price: "" }] })} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 text-zinc-300 hover:bg-white/5" data-testid="competitor-add">
              <Plus className="w-3.5 h-3.5" /> Přidat konkurenta
            </button>
            <div className="mt-3"><label className="block text-xs text-zinc-500 mb-1">Shrnutí srovnání pro zákazníka (nepovinné – zobrazí se pod tabulkou)</label><textarea rows={2} className="input-dark w-full resize-none text-sm" value={editing.compareVerdict ?? ""} onChange={(e) => setEditing({ ...editing, compareVerdict: e.target.value })} placeholder="např. Sixt nabízí Kodiaq II v základu Ambition za 28 000 Kč. Náš vůz je L&K s plnou výbavou, rok 2023, a stojí 26 000 Kč – bez úvazku a akontace." data-testid="vehicle-compare-verdict" /></div>
          </div>

          <div className="flex flex-wrap gap-4 mt-4">
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer" data-testid="vehicle-multiple">
              <input type="checkbox" className="w-4 h-4 accent-orange-500" checked={!!editing.multipleAvailable} onChange={(e) => setEditing({ ...editing, multipleAvailable: e.target.checked })} /> Více kusů skladem
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer" data-testid="vehicle-published">
              <input type="checkbox" className="w-4 h-4 accent-orange-500" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Publikováno na webu
            </label>
          </div>
        </div>

        {/* AI auto-fill equipment */}
        <div className="glass rounded-2xl p-5 border border-orange-500/20 bg-orange-500/[0.03]" data-testid="ai-fill-panel">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-orange-400" />
            <h3 className="font-['Cabinet_Grotesk'] font-bold">AI vyplnění výbavy</h3>
          </div>
          <p className="text-xs text-zinc-400 mb-3">Vlož odkaz na inzerát nebo nahraj PDF – AI vyplní výbavu i základní údaje. Vše pak můžeš upravit.</p>
          <div className="inline-flex p-1 rounded-full bg-white/5 border border-white/10 mb-3">
            <button onClick={() => setAiSource("url")} className={`px-4 py-1.5 rounded-full text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${aiSource === "url" ? "bg-orange-500 text-white" : "text-zinc-300 hover:text-white"}`} data-testid="ai-source-url"><Link2 className="w-3.5 h-3.5" /> Odkaz</button>
            <button onClick={() => setAiSource("text")} className={`px-4 py-1.5 rounded-full text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${aiSource === "text" ? "bg-orange-500 text-white" : "text-zinc-300 hover:text-white"}`} data-testid="ai-source-text"><Sparkles className="w-3.5 h-3.5" /> Text</button>
            <button onClick={() => setAiSource("file")} className={`px-4 py-1.5 rounded-full text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${aiSource === "file" ? "bg-orange-500 text-white" : "text-zinc-300 hover:text-white"}`} data-testid="ai-source-file"><FileText className="w-3.5 h-3.5" /> PDF soubor</button>
          </div>
          {aiSource === "url" ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <input className="input-dark flex-1" value={aiUrl} onChange={(e) => setAiUrl(e.target.value)} placeholder="https://www.sauto.cz/... (odkaz na inzerát)" data-testid="ai-url-input" disabled={aiLoading} />
              <button onClick={aiFillFromUrl} disabled={aiLoading} className="btn-accent inline-flex items-center justify-center gap-2 disabled:opacity-60 whitespace-nowrap" data-testid="ai-fill-url-btn">
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Vyplnit z odkazu
              </button>
            </div>
          ) : aiSource === "text" ? (
            <div className="space-y-2">
              <textarea rows={5} className="input-dark w-full resize-y" value={aiText} onChange={(e) => setAiText(e.target.value)} placeholder="Sem vlož kompletní výbavu z AI asistenta (interiér, exteriér, technologie, asistenty…). Klidně i s hvězdičkami a odrážkami – systém to srovná do sekcí s fajfkami." data-testid="ai-text-input" disabled={aiLoading} />
              <button onClick={aiFillFromText} disabled={aiLoading} className="btn-accent inline-flex items-center gap-2 disabled:opacity-60" data-testid="ai-fill-text-btn">
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Zpracovat text
              </button>
            </div>
          ) : (
            <div>
              <input ref={aiFileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => aiFillFromFile(e.target.files?.[0])} data-testid="ai-file-input" />
              <button onClick={() => aiFileRef.current?.click()} disabled={aiLoading} className="btn-accent inline-flex items-center gap-2 disabled:opacity-60" data-testid="ai-fill-file-btn">
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Nahrát PDF a vyplnit
              </button>
            </div>
          )}
          {aiLoading && <p className="text-xs text-orange-300 mt-2 inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> AI čte zdroj a připravuje výbavu… (~15 s)</p>}
        </div>

        {/* Feature groups */}
        <div className="glass rounded-2xl p-5 border border-white/10">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-['Cabinet_Grotesk'] font-bold flex items-center gap-2"><Layers className="w-4 h-4 text-orange-400" /> Výbava vozu (co auto má)</h3>
            <button onClick={addGroup} className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20" data-testid="vehicle-add-group"><Plus className="w-3 h-3" /> Přidat sekci</button>
          </div>
          {fg.length === 0 && <p className="text-sm text-zinc-500">Zatím žádná výbava. Přidejte sekci (např. „Komfort a interiér").</p>}
          <div className="space-y-4">
            {fg.map((g, gi) => (
              <div key={gi} className="rounded-xl bg-white/5 p-3 border border-white/10" data-testid={`vehicle-group-${gi}`}>
                <div className="flex items-center gap-2 mb-2">
                  <input className="input-dark flex-1 font-medium" value={g.title} onChange={(e) => setGroupTitle(gi, e.target.value)} placeholder="Název sekce (např. Komfort a interiér)" data-testid={`vehicle-group-title-${gi}`} />
                  <button onClick={() => removeGroup(gi)} className="text-red-300 hover:text-red-200 p-2" data-testid={`vehicle-group-remove-${gi}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="space-y-2 pl-1">
                  {(g.items || []).map((it, ii) => (
                    <div key={ii} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" />
                      <input className="input-dark flex-1 text-sm" value={it} onChange={(e) => setItem(gi, ii, e.target.value)} placeholder="Položka výbavy" data-testid={`vehicle-item-${gi}-${ii}`} />
                      <button onClick={() => removeItem(gi, ii)} className="text-zinc-500 hover:text-red-300 p-1"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <button onClick={() => addItem(gi)} className="text-xs text-orange-300 inline-flex items-center gap-1 mt-1" data-testid={`vehicle-add-item-${gi}`}><Plus className="w-3 h-3" /> Přidat položku</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Images */}
        <div className="glass rounded-2xl p-5 border border-white/10">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-['Cabinet_Grotesk'] font-bold flex items-center gap-2"><ImagePlus className="w-4 h-4 text-orange-400" /> Fotky vozu</h3>
            {editing.id && (
              <>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onUpload(e.target.files)} data-testid="vehicle-image-input" />
                <button onClick={() => fileRef.current?.click()} disabled={uploading} className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-60" data-testid="vehicle-upload-btn">
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Nahrát fotky
                </button>
              </>
            )}
          </div>
          {!editing.id ? (
            <p className="text-sm text-zinc-500">Nejprve vůz uložte, poté sem půjde nahrát fotky.</p>
          ) : (editing.images || []).length === 0 ? (
            <p className="text-sm text-zinc-500">Zatím žádné fotky. První fotka je hlavní (na kartě vozu). Pořadí lze měnit šipkami.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="vehicle-images">
              {editing.images.map((img, idx) => (
                <div key={img.id} className="rounded-xl overflow-hidden border border-white/10 bg-black/30" data-testid={`vehicle-image-${idx}`}>
                  <div className="relative aspect-[4/3]">
                    <img src={adminImg(img.url, "thumb")} alt={img.alt} loading="lazy" className="w-full h-full object-cover" />
                    {idx === 0 && <span className="absolute top-1.5 left-1.5 text-[10px] px-2 py-0.5 rounded-full bg-orange-500 text-white font-semibold">HLAVNÍ</span>}
                  </div>
                  <div className="p-2 space-y-1.5">
                    <input className="input-dark text-xs py-1" value={img.label || ""} placeholder="Popisek (Zepředu…)" onChange={(e) => setEditing({ ...editing, images: editing.images.map((x) => (x.id === img.id ? { ...x, label: e.target.value } : x)) })} onBlur={(e) => saveLabel(img.id, e.target.value)} data-testid={`vehicle-image-label-${idx}`} />
                    <div className="flex items-center justify-between">
                      <div className="flex gap-1">
                        <button onClick={() => moveImage(idx, -1)} disabled={idx === 0} className="p-1 text-zinc-400 hover:text-white disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                        <button onClick={() => moveImage(idx, 1)} disabled={idx === editing.images.length - 1} className="p-1 text-zinc-400 hover:text-white disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
                      </div>
                      <button onClick={() => delImage(img.id)} className="p-1 text-red-300 hover:text-red-200" data-testid={`vehicle-image-delete-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="vehicles-admin-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-['Cabinet_Grotesk'] text-xl font-bold flex items-center gap-2"><Car className="w-5 h-5 text-orange-400" /> Vozy na webu</h2>
          <p className="text-sm text-zinc-400 mt-1">Spravujte nabídku vozů na veřejném webu – cena, výbava, fotky, detaily. Změny se projeví po uložení. <span className="text-zinc-500">Pořadí změníte přetažením vozu myší (nebo šipkami).</span></p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={syncFleet} disabled={syncing} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full border border-white/15 text-zinc-300 hover:text-white disabled:opacity-60" data-testid="vehicle-sync-fleet-btn" title="Propíše všechny webové vozy do Vozového parku (pro smlouvy)">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Do vozového parku
          </button>
          <button onClick={refreshAllCompetitors} disabled={refreshingComp} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10 disabled:opacity-60" data-testid="refresh-competitors-btn" title="AI znovu odhadne ceny konkurence u všech vozů (běží i automaticky každé pondělí)">
            {refreshingComp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Obnovit srovnání cen
          </button>
          <button onClick={dedupeFleet} disabled={deduping} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full border border-white/15 text-zinc-300 hover:text-white disabled:opacity-60" data-testid="vehicle-dedupe-btn" title="Sloučí duplicitní vozy ve Vozovém parku">
            {deduping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Odstranit duplicity
          </button>
          <button onClick={optimizeImages} disabled={optimizing} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-60" data-testid="vehicle-optimize-images-btn" title="Zmenší a zkomprimuje všechny nahrané fotky vozů pro rychlé načítání webu (jednorázově, může trvat ~1 min)">
            {optimizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Zrychlit fotky
          </button>
          <button onClick={applyPricelist} disabled={applyingPl} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full border border-orange-500/40 text-orange-300 hover:bg-orange-500/10 disabled:opacity-60" data-testid="apply-pricelist-btn" title="Nastaví u aut na webu ceny vč. DPH podle finálního ceníku EffectDeal (spáruje dle názvu modelu)">
            {applyingPl ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Použít ceník (vč. DPH)
          </button>
          <button onClick={startNew} className="btn-accent inline-flex items-center gap-2" data-testid="vehicle-new-btn"><Plus className="w-4 h-4" /> Nový vůz</button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-orange-400" /></div>
      ) : list.length === 0 ? (
        <p className="text-zinc-500 text-sm">Zatím žádné vozy. Přidejte první přes „Nový vůz".</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vehicles-list">
          {list.map((v) => (
            <div key={v.id} draggable
              onDragStart={(e) => { setDragId(v.id); e.dataTransfer.effectAllowed = "move"; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDropVehicle(v.id)}
              onDragEnd={() => setDragId(null)}
              className={`glass rounded-2xl overflow-hidden border flex flex-col cursor-grab active:cursor-grabbing transition-opacity ${dragId === v.id ? "opacity-40 border-orange-500/50" : "border-white/10"}`}
              data-testid={`vehicle-row-${v.slug}`}>
              <div className="relative aspect-[4/3] bg-black/30">
                <span className="absolute top-2 left-2 z-10 w-7 h-7 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-zinc-300" title="Přetáhněte pro změnu pořadí"><GripVertical className="w-4 h-4" /></span>
                {v.images?.[0] ? (
                  <img src={adminImg(v.images[0].url, "thumb")} alt={v.name} loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-600"><Car className="w-10 h-10" /></div>
                )}
                <span className={`absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full font-semibold ${v.published ? "bg-emerald-500/80 text-white" : "bg-zinc-600/80 text-white"}`}>
                  {v.published ? "Publikováno" : "Skryto"}
                </span>
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <h3 className="font-['Cabinet_Grotesk'] font-bold">{v.name}</h3>
                <p className="text-xs text-zinc-500 mt-0.5">{[v.year, v.bodyType, v.priceMonthly ? `${Number(v.priceMonthly).toLocaleString("cs-CZ")} Kč/měs` : "cena na dotaz"].filter(Boolean).join(" · ")}</p>
                <p className="text-[11px] text-zinc-600 mt-1">{v.images?.length || 0} fotek · {(v.featureGroups || []).reduce((a, g) => a + (g.items?.length || 0), 0)} položek výbavy</p>
                <div className="flex gap-2 mt-3 pt-3 border-t border-white/5">
                  <button onClick={() => move(v, -1)} title="Posunout výš (zobrazí se dřív)" className="text-xs inline-flex items-center justify-center px-2 py-1.5 rounded-full bg-white/10 hover:bg-white/20" data-testid={`vehicle-up-${v.slug}`}><ArrowUp className="w-3 h-3" /></button>
                  <button onClick={() => move(v, 1)} title="Posunout níž" className="text-xs inline-flex items-center justify-center px-2 py-1.5 rounded-full bg-white/10 hover:bg-white/20" data-testid={`vehicle-down-${v.slug}`}><ArrowDown className="w-3 h-3" /></button>
                  <button onClick={() => startEdit(v)} className="flex-1 text-xs inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20" data-testid={`vehicle-edit-${v.slug}`}><Pencil className="w-3 h-3" /> Upravit</button>
                  <button onClick={() => togglePublish(v)} className="text-xs inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20" data-testid={`vehicle-toggle-${v.slug}`}>{v.published ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}</button>
                  <button onClick={() => del(v)} className="text-xs inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/20 text-red-200 hover:bg-red-500/40" data-testid={`vehicle-delete-${v.slug}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VehiclesAdminTab;
